'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import {
  Day,
  Station,
  Substation,
  ThreeHourForecast
} from '@/types/typo3/sections/spotsanity-weather'
import {
  BaseLink,
  Container,
  Dropdown,
  Overline,
  SectionHeaderContent
} from '@/ui/components'
import IconComponent from '@/ui/Sections/SpotSanityReports/Components/Icon'
import { memo, useEffect, useMemo, useState } from 'react'

// TODO: Remove this component?

interface Props {
  data: Station
  selectedDate: string
  stations?: Substation[]
}

const Data = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => <h4 className={cn('h4', className)}>{children}</h4>

const ItemContainer = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => <div className={cn('flex items-center', className)}>{children}</div>

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="textstyle-sm-regular text-neutral-400">{children}</p>
)

const DataWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="px-md">{children}</div>
)

const CardWeatherForecast = ({ data, selectedDate, stations }: Props) => {
  //stores
  const D = useStoreI18n.use.dictionary()

  const [currentData, setCurrentData] = useState<any>()

  useEffect(() => {
    if (selectedDate === 'today' || selectedDate === 'tomorrow') {
      setCurrentData(data?.days?.[0] || {})
    } else {
      setCurrentData(data?.days || [])
    }
  }, [selectedDate, data])

  const isThreeHourForecast = useMemo(
    () => selectedDate === 'today' || selectedDate === 'tomorrow',
    [selectedDate]
  )

  const forecastData = useMemo(
    () =>
      isThreeHourForecast && currentData ? (
        <div className="gap-xs flex flex-col">
          <div className="gap-xs grid grid-cols-1">
            {currentData?.threeHourForecasts?.map((item: ThreeHourForecast) => (
              <div
                className="p-md gap-gutter grid grid-cols-2 bg-neutral-200 text-neutral-600 md:grid-cols-3 lg:flex lg:grid-cols-6 lg:justify-between"
                key={`${currentData.date}_${item?.hourFrom}`}
              >
                <ItemContainer className="textstyle-sm-bold">
                  {item?.hourFrom}
                </ItemContainer>
                <ItemContainer>
                  <IconComponent
                    className="size-10"
                    icon={`weather_big_${item?.icon}`}
                  />
                  <DataWrapper>
                    <Data>{item?.temp}</Data>
                    <Label>Celsius</Label>
                  </DataWrapper>
                </ItemContainer>
                <ItemContainer>
                  <IconComponent
                    className="size-10"
                    icon="weather_peak_windspeed"
                  />
                  <DataWrapper>
                    <Data className="gap-2xs flex justify-center">
                      {item?.windSpeed}
                      <div
                        className="flex origin-center items-center justify-center"
                        style={{
                          transform: `rotate(${item?.windDirDegree}deg)`
                        }}
                      >
                        <IconComponent
                          className="size-6"
                          icon="weather_arrow"
                        />
                      </div>
                    </Data>
                    <Label>km/h</Label>
                  </DataWrapper>
                </ItemContainer>
                <ItemContainer>
                  <IconComponent className="size-12" icon="weather_peak_wind" />
                  <DataWrapper>
                    <Data>{item?.windSpeedMax}</Data>
                    <Label>km/h</Label>
                  </DataWrapper>
                </ItemContainer>
                <ItemContainer>
                  <IconComponent
                    className="size-11"
                    icon="weather_rainchance"
                  />
                  <DataWrapper>
                    <Data>{item?.precipProbabilityPercent}</Data>
                    <Label>%</Label>
                  </DataWrapper>
                </ItemContainer>
                <ItemContainer>
                  <IconComponent
                    className="size-13"
                    icon="weather_rain_amount"
                  />
                  <DataWrapper>
                    <Data>{item?.precipInMm}</Data>
                    <Label>%</Label>
                  </DataWrapper>
                </ItemContainer>
              </div>
            ))}
          </div>

          <div className="grid xl:grid-cols-3">
            <div className="p-md bg-neutral-200 text-neutral-600 xl:col-span-2">
              <div className="py-md gap-xs flex flex-col">
                <div className="sm:col-span-2">
                  <Overline>{D['fe.weather.currentForecast']}</Overline>
                </div>
                <div className="py-md gap-xs gap-x-2xl grid grid-cols-1 sm:grid-cols-2">
                  <div className="flex justify-between">
                    <p className="textstyle-md-bold">
                      {D['fe.weather.sunshine']}
                    </p>
                    <p className="textstyle-md-regular">
                      {currentData.sunshineDurationInMinutesText}
                    </p>
                  </div>
                  <div className="flex justify-between">
                    <p className="textstyle-md-bold">
                      {D['fe.weather.probabilityOfPrecipitationMm']}
                    </p>
                    <p className="textstyle-md-regular">
                      {currentData.precipInMmText}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="gap-xs flex flex-col">
          {currentData?.map((day: Day, index: number) => (
            <div
              className="p-md gap-gutter grid grid-cols-1 bg-neutral-200 text-neutral-600 md:grid-cols-3 lg:flex lg:grid-cols-6 lg:justify-between"
              key={`${day?.date}_${index.toString()}`}
            >
              <ItemContainer>
                <Overline>{day?.dayText},</Overline>
                <p className="text-neutral-400">{day?.dateText}</p>
              </ItemContainer>
              <ItemContainer>
                <IconComponent
                  className="h-8 w-8"
                  icon={`weather_big_${day?.icon}`}
                />
                <DataWrapper>
                  <Data>
                    {day?.minTemp} / {day?.maxTemp}
                  </Data>
                  <Label>Celsius</Label>
                </DataWrapper>
              </ItemContainer>
              <ItemContainer>
                <IconComponent
                  className="h-8 w-8"
                  icon="weather_peak_windspeed"
                />
                <DataWrapper>
                  <Data className="flex justify-center gap-2">
                    {day?.windSpeed}
                    <div
                      className="origin-center"
                      style={{ transform: `rotate(${day?.windDirDegree}deg)` }}
                    >
                      <IconComponent className="h-8 w-8" icon="weather_arrow" />
                    </div>
                  </Data>
                  <Label>km/h</Label>
                </DataWrapper>
              </ItemContainer>
              <ItemContainer>
                <IconComponent className="h-8 w-8" icon="weather_peak_wind" />
                <DataWrapper>
                  <Data>{day?.windSpeedMax}</Data>
                  <Label>km/h</Label>
                </DataWrapper>
              </ItemContainer>
              <ItemContainer>
                <IconComponent className="h-8 w-8" icon="weather_rainchance" />
                <DataWrapper>
                  <Data>{day?.precipProbabilityPercent}</Data>
                  <Label>%</Label>
                </DataWrapper>
              </ItemContainer>
              <ItemContainer>
                <IconComponent className="h-8 w-8" icon="weather_rain_amount" />
                <DataWrapper>
                  <Data>{day?.precipInMmText}</Data>
                  <Label>mm</Label>
                </DataWrapper>
              </ItemContainer>
            </div>
          ))}
        </div>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentData, isThreeHourForecast]
  )

  return (
    <div className="">
      <SectionHeaderContent as="h2">
        {D['fe.weather.automatedForecast']}
        &nbsp;
        {data?.stationName}
      </SectionHeaderContent>
      <Container>
        {stations && stations?.length ? (
          <div className="mb-xs grid">
            <Dropdown
              className="relative"
              body={
                stations?.length &&
                stations?.map((item, index) => (
                  <BaseLink
                    key={index?.toString()}
                    href={item.href}
                    nextLink={true}
                    aria-label="link"
                  >
                    {item.title}
                  </BaseLink>
                ))
              }
              head={data?.stationName}
              label={D['fe.weather.location']}
            />
          </div>
        ) : undefined}
        <div>{forecastData}</div>
      </Container>
    </div>
  )
}

export default memo(CardWeatherForecast)
